import { GenericVideoEmbed } from './GenericVideoEmbed';
import { Heading, Box } from '../../../../../../mads';
import { HtmlContent } from '../../../../../../shared/components/HtmlContent';
import { YouTubeVideoPlayer } from '../../HtmlContent/YouTubeVideoPlayer';

import { spacing } from '@indeed/ifl-tokens/react-native';
import React, { useState, useEffect } from 'react';
import { Image, StyleSheet } from 'react-native';

export type VideoSource =
    | {
          type: 'youtube';
          videoId: string;
      }
    | {
          type: 'iframe';
          videoUrl: string;
      };

interface CompanyCustomContentProps {
    title: string;
    videoUrl?: string;
    imageUrl?: string;
    description: string;
    altText?: string;
    isSimpleVjImproveActive?: boolean;
}

/**
 * Renders an image with auto-calculated aspect ratio based on original dimensions
 */
function DynamicImage({ imageUrl, altText }: { imageUrl: string; altText?: string }) {
    const [aspectRatio, setAspectRatio] = useState<number | undefined>(undefined);

    useEffect(() => {
        // Get the actual image dimensions to calculate aspect ratio
        Image.getSize(
            imageUrl,
            (width, height) => {
                setAspectRatio(width / height);
            },
            (error) => {
                console.error('Failed to get image size:', error);
                // Fallback to 16:9 if image size can't be determined
                setAspectRatio(16 / 9);
            },
        );
    }, [imageUrl]);

    // Don't render until we have the aspect ratio
    if (!aspectRatio) {
        return null;
    }

    return (
        <Image
            source={{ uri: imageUrl }}
            style={[styles.image, { aspectRatio }]}
            accessibilityLabel={altText || ''}
            resizeMode="contain"
        />
    );
}

/**
 * Extracts YouTube video ID from various URL formats
 * Returns the video ID or null if the URL is not a valid YouTube URL
 */
function extractYouTubeVideoId(url: string): string | null {
    if (url.includes('youtube.com/embed/')) {
        return url.split('youtube.com/embed/')[1].split('?')[0];
    } else if (url.includes('youtube.com/watch?v=')) {
        return url.split('v=')[1].split('&')[0];
    } else if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1].split('?')[0];
    }
    return null;
}

/**
 * Determines the video source type from a URL
 * Returns VideoSource object or null if the URL is invalid or malformed
 */
export function getVideoSourceFromUrl(url: string): VideoSource | null {
    // Validate URL format - URL constructor will throw for empty, null, whitespace, or malformed URLs
    try {
        // eslint-disable-next-line no-new -- URL.canParse isn't yet implemented by Hermes
        new URL(url);
    } catch (error) {
        console.error('Failed get video source from url: Invalid URL', url);
        return null;
    }

    const youtubeVideoId = extractYouTubeVideoId(url);
    if (youtubeVideoId) {
        return {
            type: 'youtube',
            videoId: youtubeVideoId,
        };
    }

    // For any other valid video URL, treat it as a generic iframe embed
    return {
        type: 'iframe',
        videoUrl: url,
    };
}

export function CompanyCustomContent({
    title,
    videoUrl,
    imageUrl,
    description,
    altText,
}: CompanyCustomContentProps) {
    const videoSource = videoUrl ? getVideoSourceFromUrl(videoUrl) : null;

    return (
        <Box sx={styles.container}>
            <Heading level={4} sx={styles.heading}>
                {title}
            </Heading>

            {imageUrl ? (
                <DynamicImage imageUrl={imageUrl} altText={altText} />
            ) : videoSource ? (
                videoSource.type === 'youtube' ? (
                    <YouTubeVideoPlayer videoId={videoSource.videoId} />
                ) : (
                    <GenericVideoEmbed videoUrl={videoSource.videoUrl} />
                )
            ) : null}

            <Box sx={styles.descriptionContainer}>
                <HtmlContent>
                    {"<div style='white-space: pre-line'>" + description + '</div>'}
                </HtmlContent>
            </Box>
        </Box>
    );
}

const styles = StyleSheet.create({
    container: {
        marginTop: spacing.s2,
    },
    heading: {
        fontSize: 18, // Matches web's 1.125rem
        marginBottom: spacing.s2,
    },
    image: {
        width: '100%',
    },
    descriptionContainer: {
        marginBlock: spacing.s4,
    },
});
